import { Grid, Checkbox, FormControlLabel, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { BirthdayDocument } from 'shell/view-models/birthday-document';
import { PmiButton } from 'shell/components/shared/button';
import { memo } from 'react';
import PmiDatePicker from 'shell/components/shared/date-picker';
import MunicipalityList from 'shell/components/shared/municipality-list';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';

interface BirthdayDocumentFormProps {
  values?: BirthdayDocument;
  onChange: (key: keyof BirthdayDocument, value: BirthdayDocument[keyof BirthdayDocument]) => void;
  onChangeValues: (values: BirthdayDocument) => void;
  errors?: FormikErrors<BirthdayDocument>;
  touched?: FormikTouched<BirthdayDocument>;
  disabled?: boolean;
  birthday?: DateTime | null;
}

const BirthdayDocumentFormComponent = ({
  values,
  onChange,
  errors,
  touched,
  onChangeValues,
  disabled = false,
  birthday,
}: BirthdayDocumentFormProps): React.ReactElement => {
  const resetForm = useEventCallback(() => {
    onChangeValues({});
  });

  const formContext = useFormikContext<NewPersonVM>();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <MunicipalityList
          aria-label="Comune"
          label="Comune"
          id="municipality"
          value={values?.municipality || null}
          onChange={(municipality) => onChange('municipality', municipality)}
          error={touched?.municipality && Boolean(errors?.municipality)}
          disabled={disabled}
          fromDate={birthday}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          aria-label="Provincia"
          label="Provincia"
          id="province"
          name="province"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.municipality?.province || ''}
          onChange={(event) => onChange('province', event.target.value)}
          disabled
        />
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <PmiDatePicker
            label="Data atto"
            aria-label="Data atto"
            value={values?.documentDate || null}
            onChange={(date) => {
              onChange('documentDate', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="birthdayDocument.documentDate"
                fullWidth
                onBlur={formContext?.handleBlur}
                error={touched?.documentDate && Boolean(errors?.documentDate)}
                helperText={errors?.documentDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={4} display="flex" alignSelf="center">
          <FormControlLabel
            control={
              <Checkbox
                id="transcript"
                checked={values?.transcript || false}
                onChange={() => onChange('transcript', !values?.transcript)}
                inputProps={{ 'aria-label': 'senza fissa dimora' }}
                disabled={disabled}
              />
            }
            label="Trascritto"
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Anno"
            label="Anno"
            id="year"
            name="birthdayDocument.year"
            fullWidth
            onBlur={formContext?.handleBlur}
            type="number"
            variant="outlined"
            value={values?.year || ''}
            onChange={(event) => onChange('year', event.target.value)}
            error={touched?.year && Boolean(errors?.year)}
            helperText={errors?.year}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Numero Atto"
            label="Nr. Atto"
            id="documentNumber"
            name="birthdayDocument.documentNumber"
            fullWidth
            type="text"
            onBlur={formContext?.handleBlur}
            variant="outlined"
            value={values?.documentNumber || ''}
            onChange={(event) => onChange('documentNumber', event.target.value)}
            error={touched?.documentNumber && Boolean(errors?.documentNumber)}
            helperText={errors?.documentNumber}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Parte"
            label="Parte"
            id="portion"
            name="birthdayDocument.portion"
            fullWidth
            onBlur={formContext?.handleBlur}
            type="text"
            variant="outlined"
            inputProps={{
              maxLength: '1',
            }}
            value={values?.portion || ''}
            onChange={(event) => onChange('portion', event.target.value)}
            error={touched?.portion && Boolean(errors?.portion)}
            helperText={errors?.portion}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Serie"
            label="Serie"
            id="serie"
            name="birthdayDocument.serie"
            fullWidth
            onBlur={formContext?.handleBlur}
            type="text"
            inputProps={{
              maxLength: '1',
            }}
            variant="outlined"
            value={values?.serie || ''}
            onChange={(event) => onChange('serie', event.target.value)}
            error={touched?.serie && Boolean(errors?.serie)}
            helperText={errors?.serie}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            aria-label="Volume"
            label="Volume"
            id="volume"
            name="birthdayDocument.volume"
            fullWidth
            type="text"
            onBlur={formContext?.handleBlur}
            variant="outlined"
            value={values?.volume || ''}
            onChange={(event) => onChange('volume', event.target.value)}
            error={touched?.volume && Boolean(errors?.volume)}
            helperText={errors?.volume}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={0} xs={12} md={6}>
        <PmiTextField
          aria-label="Ufficio Municipio"
          label="Ufficio Municipio"
          id="townHallOffice"
          name="birthdayDocument.townHallOffice"
          fullWidth
          onBlur={formContext?.handleBlur}
          type="text"
          variant="outlined"
          value={values?.townHallOffice || ''}
          onChange={(event) => onChange('townHallOffice', event.target.value)}
          error={touched?.townHallOffice && Boolean(errors?.townHallOffice)}
          helperText={errors?.townHallOffice}
          inputProps={{
            maxLength: '50',
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid container item spacing={2} xs={12}>
        <Grid item xs={12} md={2}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            fullWidth
            onClick={resetForm}
            id="reset-birthday-document"
            disabled={disabled}
          >
            Ripulisci
          </PmiButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

BirthdayDocumentFormComponent.displayName = 'Birthday document component';

const BirthdayDocumentForm = memo(BirthdayDocumentFormComponent);

export default BirthdayDocumentForm;
